import { Plan, PlanAssignment, PlanRecord } from '../models/plan';
import { Dates } from './dates';
import { CurrentDateTime } from '../models/current-date-time';
import { PlanStatuses, PlanSubjectId, SubjectId } from '../resources/config';
import { User, UserId } from '../models/user';
import { UserUtil } from './user-util';
import { SubjectUtil } from './subject-util';

export interface PriorityRecord {
  status: string;
  record: PlanRecord;
}

export interface UserAssignmentPlan {
  plan: Plan;
  status: string;
  assignmentSubjectIds: string[];
}

export class PlanUtil {
  static getRecordStatus(record: PlanRecord, currentDateTime: CurrentDateTime): string {
    let status: string = PlanStatuses.UNASSIGNED;
    const now: string = Dates.stdDateStringFromIso(currentDateTime.dateTime);
    const startAt: string = Dates.stdDateStringFromIso(record.startAt + ' 00:00:00');
    const endAt: string = Dates.stdDateStringFromIso(record.endAt + ' 23:59:59');

    if (endAt >= now && startAt <= now) {
      status = PlanStatuses.ACTIVE;
    } else if (endAt < now && (status === PlanStatuses.ENDED || status === PlanStatuses.UNASSIGNED)) {
      status = PlanStatuses.ENDED;
    } else if (startAt > now) {
      status = PlanStatuses.RESERVE;
    }

    return status;
  }

  static getPriorityRecord(records: PlanRecord[], currentDateTime: CurrentDateTime): PriorityRecord {
    const recordsForSort = [...records];
    let status: string = PlanStatuses.UNASSIGNED;
    let reserveRecord: PlanRecord = null;
    if (recordsForSort.length === 0) return { status, record: null };

    recordsForSort.sort((a, b) => {
      if (a.id > b.id) return 1;
      if (a.id < b.id) return -1;
      return 0;
    });
    for (const record of recordsForSort) {
      status = this.getRecordStatus(record, currentDateTime);
      if (status === PlanStatuses.ACTIVE) {
        return { status, record };
      } else if (status === PlanStatuses.RESERVE) {
        reserveRecord = record;
      }
    }

    if (reserveRecord !== null) return { status: PlanStatuses.RESERVE, record: reserveRecord };

    return { status, record: recordsForSort.slice(-1)[0] };
  }

  static getAssignmentMemberCount(record: PlanRecord): number {
    let count = 0;

    for (const assign of record.assignment) {
      count = count + assign.memberIds.length;
    }

    return count;
  }

  static getAssignmentUserNames(record: PlanRecord, users: User[]): string[] {
    const userNames: string[] = [];
    for (const assign of record.assignment) {
      for (const memberId of assign.memberIds) {
        userNames.push(UserUtil.getUserName(users, memberId));
      }
    }

    return userNames;
  }

  static getUserAssignmentPlan(userId: UserId, plans: Plan[], currentDateTime: CurrentDateTime): UserAssignmentPlan | null {
    const assignPlans: Plan[] = [];
    plans.forEach(plan => {
      plan.records.forEach(record => {
        if (record.assignment.filter(assignment => assignment.memberIds.includes(userId)).length > 0) {
          assignPlans.push(plan);
        }
      });
    });

    if (assignPlans.length === 0) return null;

    let assignPlan: Plan;
    let assignmentSubjectIds: string[];
    let assignPlanStatus: string = PlanStatuses.UNASSIGNED;
    for (const plan of assignPlans) {
      const priorityRecord: PriorityRecord = this.getPriorityRecord(plan.records, currentDateTime);
      const assignments: PlanAssignment[] = priorityRecord.record.assignment.filter(assignment => assignment.memberIds.includes(userId));

      if (assignments.length < 1) {
        continue;
      }

      if (priorityRecord.status === PlanStatuses.ACTIVE) {
        assignPlan = plan;
        assignmentSubjectIds = assignments.map(assignment => assignment.subjectId);
        assignPlanStatus = PlanStatuses.ACTIVE;
        break;
      } else if (priorityRecord.status === PlanStatuses.RESERVE) {
        if (assignments.length > 0) {
          assignPlan = plan;
          assignmentSubjectIds = assignments.map(assignment => assignment.subjectId);
          assignPlanStatus = PlanStatuses.RESERVE;
        }
      } else if (assignPlanStatus !== PlanStatuses.RESERVE) {
        if (assignments.length > 0) {
          assignPlan = plan;
          assignmentSubjectIds = assignments.map(assignment => assignment.subjectId);
          assignPlanStatus = priorityRecord.status;
        }
      }
    }

    if (assignPlan === undefined || assignmentSubjectIds === undefined) {
      return null;
    }

    return {
      plan: assignPlan,
      status: assignPlanStatus,
      assignmentSubjectIds
    };
  }

  static getAssignmentUsers(record: PlanRecord, users: User[]): User[] {
    const assignmentUsers: User[] = [];
    for (const assign of record.assignment) {
      for (const memberId of assign.memberIds) {
        for (const user of users) if (user.id === memberId) assignmentUsers.push(user);
      }
    }

    return assignmentUsers;
  }

  /**
   * ペアとなるプラン教科 ID を返す。ペア対象外のプランの場合は null を返す。
   *
   * 全科目プラン(99) と 全科目＋推薦・総合プラン(97) は、同一学校の同一契約系列を表す 2 つの
   * プランドキュメントとして扱う（プラン切替時に割当を引き継ぐため、両方に同じメンバーが載る）。
   */
  static getPairedPlanSubjectId(subjectId: string): string | null {
    if (subjectId === PlanSubjectId.ALL) return PlanSubjectId.ALL_WITH_SPECIAL_ADMISSIONS;
    if (subjectId === PlanSubjectId.ALL_WITH_SPECIAL_ADMISSIONS) return PlanSubjectId.ALL;
    return null;
  }

  static isAssignableUsers(
    assignPlan: Plan,
    user: User,
    plans: Plan[],
    currentDateTime: CurrentDateTime,
    assignmentSubjectId: string = null
  ): boolean {
    const priorityRecord = this.getPriorityRecord(assignPlan.records, currentDateTime);
    const now: string = Dates.stdDateStringFromIso(currentDateTime.dateTime);

    // 割当は対象プランの未終了レコード全件に反映される（サーバーの assign-plan-use-case が予約レコードにもコピーする）ため、
    // ペアプランの重複判定もこの全期間を対象にする
    const assignTargetRecords: PlanRecord[] = assignPlan.records.filter(
      record => Dates.stdDateStringFromIso(record.endAt + ' 23:59:59') >= now
    );
    if (assignTargetRecords.length === 0) assignTargetRecords.push(priorityRecord.record);

    // 全科目 ⇔ 全科目＋推薦・総合はペアプラン（同一契約系列）として扱う
    const pairedPlanSubjectId = this.getPairedPlanSubjectId(assignPlan.subjectId);

    let isAssigned = false;

    for (const plan of plans) {
      // アクティブまたは予約
      const records = plan.records.filter(record => {
        const startAt: string = Dates.stdDateStringFromIso(record.startAt + ' 00:00:00');
        const endAt: string = Dates.stdDateStringFromIso(record.endAt + ' 23:59:59');
        return (endAt >= now && startAt <= now) || startAt > now;
      });

      // 期間が重ならないペアプランへの割当は「他プランへの割当」とみなさない
      const isPairedPlan =
        pairedPlanSubjectId !== null &&
        plan.id !== assignPlan.id &&
        plan.subjectId === pairedPlanSubjectId &&
        plan.organization === assignPlan.organization &&
        plan.schoolId === assignPlan.schoolId;

      // アサイン可能かどうかのチェック
      const maxNumForAllWithSpecialAdmissionsPlan = Object.keys(SubjectId).length; // 全科目プラン＋推薦・総合プランの場合は全科目数
      const maxNumForAllPlan = SubjectUtil.getSubjectIds().filter(id => id !== SubjectId.SPECIAL_ADMISSIONS).length; // 全科目プランの場合は推薦・総合が不要なため、上記のマイナス１の数

      for (const record of records) {
        const assignments = record.assignment.filter(assignment => assignment.memberIds.includes(user.id));

        // 全教科&同一のプランの場合はプラン内でアサイン可となるようにする
        if (
          (assignPlan.subjectId === PlanSubjectId.ALL || assignPlan.subjectId === PlanSubjectId.ALL_WITH_SPECIAL_ADMISSIONS) &&
          plan.id === assignPlan.id
        ) {
          if (
            (assignPlan.subjectId === PlanSubjectId.ALL && assignments.length === maxNumForAllPlan) ||
            (assignPlan.subjectId === PlanSubjectId.ALL_WITH_SPECIAL_ADMISSIONS &&
              assignments.length === maxNumForAllWithSpecialAdmissionsPlan)
          ) {
            isAssigned = true;
            break;
          }

          if (assignmentSubjectId !== null) {
            const sameSubjectAssignments = assignments.filter(assignment => assignment.subjectId === assignmentSubjectId);
            if (sameSubjectAssignments.length > 0) {
              isAssigned = true;
              break;
            }
          }
        } else {
          if (isPairedPlan && !this.isOverlappedWithAnyPeriod(record, assignTargetRecords)) {
            // ペアプランかつ割当対象プランのどのレコードとも期間が重ならない場合は割当済みとみなさない
            continue;
          }

          if (assignments.length > 0) {
            isAssigned = true;
            break;
          }
        }
      }

      if (isAssigned) {
        break;
      }
    }

    return !isAssigned;
  }

  /**
   * プランレコードの期間が、いずれかのプランレコードの期間と重なっているかを返す。
   */
  private static isOverlappedWithAnyPeriod(record: PlanRecord, others: PlanRecord[]): boolean {
    const startAt: string = Dates.stdDateStringFromIso(record.startAt + ' 00:00:00');
    const endAt: string = Dates.stdDateStringFromIso(record.endAt + ' 23:59:59');

    return others.some(other => {
      const otherStartAt: string = Dates.stdDateStringFromIso(other.startAt + ' 00:00:00');
      const otherEndAt: string = Dates.stdDateStringFromIso(other.endAt + ' 23:59:59');

      return !(endAt < otherStartAt || startAt > otherEndAt);
    });
  }
}
